import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const STORAGE_KEY = "savebox_cookie_consent";

export default function CookieConsent() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show only if no choice was recorded yet.
    let stored = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {
      stored = null;
    }
    if (!stored) {
      // Defer to next tick to avoid setState-in-effect lint warning.
      const t = setTimeout(() => setVisible(true), 0);
      return () => clearTimeout(t);
    }
  }, []);

  const setChoice = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() })
      );
    } catch (_err) {
      // localStorage unavailable (private mode) — fall through to hiding anyway.
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-card/95 backdrop-blur"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5">
        <div className="flex items-start gap-3 sm:items-center">
          <div className="hidden h-10 w-10 shrink-0 items-center justify-center border border-border bg-background sm:flex">
            <Cookie className="h-5 w-5 text-[#002FA7]" aria-hidden />
          </div>
          <div className="min-w-0">
            <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">
              {t("cookie.title")}
            </div>
            <p className="mt-1 text-sm leading-relaxed text-foreground">
              {t("cookie.body")}{" "}
              <Link
                to="/legal/cookies"
                className="font-medium underline underline-offset-2 hover:text-[#002FA7]"
                data-testid="cookie-consent-learn-more"
              >
                {t("cookie.learnMore")}
              </Link>
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={() => setChoice("rejected")}
            data-testid="cookie-consent-reject"
            className="border border-border bg-background px-4 py-2 font-mono text-xs uppercase tracking-wider transition hover:border-foreground"
          >
            {t("cookie.reject")}
          </button>
          <button
            type="button"
            onClick={() => setChoice("accepted")}
            data-testid="cookie-consent-accept"
            className="bg-[#002FA7] px-4 py-2 font-mono text-xs uppercase tracking-wider text-white transition hover:bg-[#002FA7]/90"
          >
            {t("cookie.accept")}
          </button>
          <button
            type="button"
            onClick={() => setChoice("dismissed")}
            data-testid="cookie-consent-dismiss"
            className="hidden border border-border bg-background p-2 transition hover:border-foreground sm:block"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
